import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

/**
 * Helper to get access token from localStorage
 */
function getStoredAccessToken() {
  const storageKey = `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`;
  const stored = localStorage.getItem(storageKey);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      return parsed.access_token;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Helper for authenticated REST API calls
 */
async function restApi(endpoint, options = {}) {
  const accessToken = getStoredAccessToken();
  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  const url = `${supabaseUrl}/rest/v1/${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      'apikey': supabaseAnonKey,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'Prefer': options.method === 'PATCH' || options.method === 'POST' ? 'return=representation' : undefined,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error: ${response.status} - ${errorText}`);
  }

  // Handle empty responses (like DELETE)
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

/**
 * Sign up a new user
 */
export async function signUp(email, password) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/login`
    }
  });

  if (error) throw error;
  return data;
}

/**
 * Send password reset email
 */
export async function resetPassword(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  });

  if (error) throw error;
}

/**
 * Update user's password (after clicking reset link)
 */
export async function updatePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({
    password: newPassword
  });

  if (error) throw error;
}

/**
 * Sign in an existing user
 */
export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  return data;
}

/**
 * Sign out the current user
 */
export function signOut() {
  // Clear localStorage to ensure clean state
  const storageKey = `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`;
  localStorage.removeItem(storageKey);

  // Fire and forget - don't await as it may hang
  supabase.auth.signOut().catch(() => {});
}

/**
 * Get the current user session
 */
export async function getSession() {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
}

/**
 * Get user profile with subscription info
 */
export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error && error.code === 'PGRST116') {
    return null;
  }

  if (error) throw error;
  return data;
}

/**
 * Update user's request count
 */
export async function incrementRequestCount(userId) {
  return restApi('rpc/increment_request_count', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
}

/**
 * Check if user can make a request
 */
export async function canMakeRequest(userId) {
  const profile = await getUserProfile(userId);
  // Check for unlimited access
  if (profile.unlimited_until && new Date(profile.unlimited_until) > new Date()) {
    return true;
  }
  return profile.requests_used < profile.requests_limit;
}

/**
 * Save research results
 */
export async function saveResearch(userId, research) {
  const data = await restApi('research', {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      user_prompt: research.userPrompt,
      market_definition: research.marketDefinition,
      search_terms: research.searchTerms,
      companies: research.companies,
      capabilities: research.capabilities,
      features: research.features,
    }),
  });
  return data?.[0];
}

/**
 * Get user's research history
 */
export async function getResearchHistory(userId) {
  const { data, error } = await supabase
    .from('research')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

// ==========================================
// Admin functions
// ==========================================

/**
 * Get all users (admin only)
 */
export async function getAllUsers() {
  return restApi('profiles?select=*&order=created_at.desc');
}

/**
 * Grant unlimited access to a user (admin only)
 */
export async function grantUnlimitedAccess(userId, untilDate) {
  const data = await restApi(`profiles?id=eq.${userId}`, {
    method: 'PATCH',
    body: JSON.stringify({ unlimited_until: untilDate }),
  });
  return data?.[0];
}

/**
 * Revoke unlimited access from a user (admin only)
 */
export async function revokeUnlimitedAccess(userId) {
  const data = await restApi(`profiles?id=eq.${userId}`, {
    method: 'PATCH',
    body: JSON.stringify({ unlimited_until: null }),
  });
  return data?.[0];
}

// ==========================================
// Research Cache functions
// ==========================================

const CACHE_MAX_AGE_DAYS = 30;

/**
 * Get all cached research entries with optional filtering
 * @param {string} filter - Optional search filter for query text
 * @param {number} limit - Max number of results (default 100)
 * @returns {Array} - List of cache entries
 */
export async function getAllCachedResearch(filter = '', limit = 100) {
  let endpoint = `research_cache?select=id,query_hash,original_query,market_name,updated_at&order=updated_at.desc&limit=${limit}`;

  if (filter) {
    endpoint += `&or=(original_query.ilike.%25${encodeURIComponent(filter)}%25,market_name.ilike.%25${encodeURIComponent(filter)}%25)`;
  }

  const data = await restApi(endpoint);
  return data || [];
}

/**
 * Delete specific cache entries by their IDs
 * @param {string[]} ids - Array of cache entry IDs to delete
 * @returns {boolean} - True if successful
 */
export async function deleteCacheEntries(ids) {
  await restApi(`research_cache?id=in.(${ids.join(',')})`, {
    method: 'DELETE',
  });
  return true;
}

/**
 * Check cache for existing research results
 * Returns null if not found or data is older than 30 days
 * @param {string} queryHash - Hash of the normalized query
 * @returns {Object|null} - Cached research data or null
 */
export async function getCachedResearch(queryHash) {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - CACHE_MAX_AGE_DAYS);

  try {
    const data = await restApi(
      `research_cache?query_hash=eq.${encodeURIComponent(queryHash)}&updated_at=gte.${encodeURIComponent(cutoffDate.toISOString())}&select=*`
    );
    return data?.[0] || null;
  } catch {
    return null;
  }
}

/**
 * Save or update research results in cache
 * Uses upsert to either insert new or update existing cache entry
 * @param {string} queryHash - Hash of the normalized query
 * @param {string} originalQuery - The original user query
 * @param {Object} research - The research results to cache
 * @returns {boolean} - True if successful
 */
export async function saveToCache(queryHash, originalQuery, research) {
  try {
    await restApi('research_cache', {
      method: 'POST',
      headers: {
        'Prefer': 'resolution=merge-duplicates',
      },
      body: JSON.stringify({
        query_hash: queryHash,
        original_query: originalQuery,
        market_definition: research.marketDefinition,
        market_name: research.marketName,
        search_terms: research.searchTerms,
        companies: research.companies,
        capabilities: research.capabilities,
        features: research.features,
        updated_at: new Date().toISOString()
      }),
    });
    return true;
  } catch (error) {
    console.error('Failed to save to cache:', error);
    return false;
  }
}
